
import { ICountry, ICurrency } from '@/api/types/catalogue';
import SvgFalse from "@/assets/icons/false-icon.svg";
import SvgTrue from "@/assets/icons/true-icon.svg";
import Status from '@/components/base/Status.vue';
import SaveBtnTooltip from '@/components/base/buttons/SaveBtnTooltip.vue';
import TrashBtn from "@/components/base/buttons/TrashBtn.vue";
import AutocompleteFilter from "@/components/base/form/AutocompleteFilter.vue";
import AutocompleteListItem from "@/components/base/form/AutocompleteListItem.vue";
import ComboboxArrayDesign from "@/components/base/form/ComboboxArrayDesign.vue";
import ComboboxDesign from "@/components/base/form/ComboboxDesign.vue";
import CompareFilter from "@/components/base/form/CompareFilter.vue";
import MultiselectDesign from "@/components/base/form/MultiselectDesign.vue";
import SelectDesign from "@/components/base/form/SelectDesign.vue";
import DateRange from '@/components/base/pickers/DateRange.vue';
import MonthPicker from "@/components/base/pickers/MonthPicker.vue";
import MonthPickerRange from "@/components/base/pickers/MonthPickerRange.vue";
import WeekPicker from "@/components/base/pickers/WeekPicker.vue";
import { filterClass } from '@/services/filters/filterClass';
import { IFilter } from '@/types';
import { getFormatDatesForDatepicker } from '@/utils/formatDates';
import groupBy from 'lodash-es/groupBy';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const catalogue = namespace('catalogueModule');

interface IFilterNameGroup {
    colNameKey: string;
    displayTitle: string | number | undefined;
    filters: IFilter[];
}

interface IFilterColBlock {
    colKey: string;
    nameGroups: IFilterNameGroup[];
}

@Component({
    components: {
        CompareFilter,
        AutocompleteFilter,
        WeekPicker,
        SvgTrue,
        SvgFalse,
        MonthPicker,
        MonthPickerRange,
        ComboboxArrayDesign,
        ComboboxDesign,
        SelectDesign,
        AutocompleteListItem,
        MultiselectDesign,
        Status,
        SaveBtnTooltip,
        DateRange,
        TrashBtn,
    },
})

export default class FiltersBaseDesign extends Vue {
    @Prop({ type: String, default: 'Фильтры' }) readonly title!: string;
    @Prop({ type: Array, default: () => [], required: true }) readonly filters!: IFilter[];
    @Prop({ default: false }) readonly isGroupBy!: boolean;
    @Prop({ type: Object, default: () => ({}) }) readonly selectedFilters!: object; // for chips
    @Prop({ type: String, default: 'Применить' }) readonly successBtnText!: string;
    @Prop({ type: String, default: 'Сбросить' }) readonly cancelBtnText!: string;
    @Prop({ type: Number, default: 4 }) readonly sizeLG!: number;
    @Prop({ type: Number, default: 4 }) readonly sizeXL!: number;
    @Prop({ type: Number, default: 6 }) readonly sizeMD!: number;
    @Prop({ type: Number, default: 6 }) readonly sizeSM!: number;
    @Prop({ type: Number, default: 6 }) readonly sizeMain!: number;
    @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
    @Prop({ type: Boolean, default: true }) readonly isSuccessBtn!: boolean;
    @Prop({ type: Boolean, default: true }) readonly isUsedBlurAutocomplete!: boolean;
    @Prop({ type: Boolean, default: true }) readonly isCancelBtn!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isTrashBtn!: boolean;
    @Prop({ type: Boolean, default: true }) readonly isClearable!: boolean;
    @Prop({ type: Boolean, default: false }) readonly needTooltip!: boolean;
    @Prop({ type: Array, default: () => [] }) readonly showColumnTitle!: (string | number)[];
    @Prop({ type: Object, required: false, default: () => ({}) }) readonly emptyConditions!: object;
    @Prop({ type: String, default: 'top' }) readonly tooltipPosition!: string;
    @Prop() readonly disabledDates!: any;
    @catalogue.Getter('GET_SORTED_CURRENCIES') currencies!: ICurrency[];
    @catalogue.Getter('GET_COUNTRIES') countries!: ICountry[];

    // for data-picker
    visibleDatePickers = {};
    filterClass = filterClass;

    get getAttachClass(): string | boolean {
        const noAttachRoutes = ['extended-report-advertiser', 'postback-errors'];
        if (this.$vuetify.breakpoint.width <= 890 || noAttachRoutes.includes(this.$route.name ?? '')) {
            return false;
        } else {
            return '.data-range__input-calendar-false';
        }
    }

    getContentClass(id: string): string {
        const baseClasses = 'data-range__content data-range__content-filter';
        if (this.$route.name === 'extended-report-advertiser') return baseClasses + ' advertiser-date' + ` ${id}`;
        return this.$route.name?.includes('extended-report') || this.$route.name === 'debtors'
            ? baseClasses + ` ${id}` : baseClasses;
    }

    changeExcluded(filter: IFilter): void {
        if (filter.isExcluded !== undefined) {
            filter.isExcluded = !filter.isExcluded;
        }
    }

    getFormatDatesForDatepicker = getFormatDatesForDatepicker;

    changeDialogStyle(isShowMenu: boolean, id: string): void {
        const filtersDialog = document.querySelector('.filters-dialog.v-dialog--active') as HTMLElement;
        this.visibleDatePickers[id] = isShowMenu;
        if (!!filtersDialog && isShowMenu) {
            filtersDialog.style.setProperty('overflow-x', 'visible', 'important');
        } else if (Object.values(this.visibleDatePickers).every(i => !i)) {
            filtersDialog?.style!.removeProperty('overflow-x');
        }
    }

    changeMultiselectItems({ type, items }: any): void {
        const currentFilter = this.filters.find((filter) => filter.id === type);
        if (currentFilter) {
            currentFilter.select = items;
        }
    }

    clear(filter: IFilter): void {
        filter.clear();
    }

    get hasError(): boolean {
        return Object.keys(this.emptyConditions).length > 0 && this.needTooltip;
    }

    get disableSubmitBtn(): boolean {
        return this.$props.disabled || this.hasError;
    }

    /**
     * При isGroupBy: колонки сортируются по `col`, внутри — подгруппы с заголовком `colName`.
     * Без группировки — одна колонка со всеми фильтрами.
     */
    get layoutColumns(): IFilterColBlock[] {
        if (!this.isGroupBy) {
            return [{
                colKey: 'single',
                nameGroups: [{
                    colNameKey: 'all',
                    displayTitle: undefined,
                    filters: this.filters,
                }],
            }];
        }

        const byCol = groupBy(this.filters, (f) =>
            f.col === undefined || f.col === null ? '__no_col__' : String(f.col),
        );

        const colKeys = Object.keys(byCol).sort((a, b) => {
            if (a === '__no_col__') return 1;
            if (b === '__no_col__') return -1;
            const na = Number(a);
            const nb = Number(b);
            if (!Number.isNaN(na) && !Number.isNaN(nb)) {
                return na - nb;
            }
            return a.localeCompare(b);
        });

        return colKeys.map((colKey) => {
            const colFilters = byCol[colKey];
            const nameOrder: string[] = [];
            colFilters.forEach((f) => {
                const key =
                    f.colName === undefined || f.colName === null
                        ? '__unnamed__'
                        : String(f.colName);
                if (!nameOrder.includes(key)) {
                    nameOrder.push(key);
                }
            });

            const nameGroups: IFilterNameGroup[] = nameOrder.map((key) => {
                const filtersInGroup = colFilters.filter((f) => {
                    const fk =
                        f.colName === undefined || f.colName === null
                            ? '__unnamed__'
                            : String(f.colName);
                    return fk === key;
                });
                const displayTitle =
                    key === '__unnamed__' ? undefined : filtersInGroup[0]?.colName;
                return {
                    colNameKey: key,
                    displayTitle,
                    filters: filtersInGroup,
                };
            });

            return { colKey, nameGroups };
        });
    }

    isColumnBlockHidden(columnBlock: IFilterColBlock): boolean {
        return columnBlock.nameGroups.every((ng) =>
            ng.filters.every((f) => f.isHidden),
        );
    }

    shouldShowGroupTitle(displayTitle: string | number | undefined): boolean {
        if (displayTitle === undefined || displayTitle === null) {
            return false;
        }
        return this.showColumnTitle.includes(displayTitle as string & number);
    }

    formatGroupTitle(displayTitle: string | number | undefined): string {
        if (displayTitle === undefined || displayTitle === null) {
            return '';
        }
        return displayTitle === '-' ? '' : String(displayTitle);
    }

    created(): void {
        this.filters.forEach((fl) => {
            if (fl.isCurrencyCatalogue) {
                fl.items = this.currencies;
                fl.template = this.getCurrenciesTemplate;
            }
            if (fl.isCountryCatalogue) {
                fl.items = this.countries;
            }
        });
    }

    getCurrenciesTemplate(item: ICurrency): string {
        return item.code;
    }

    submitFilters(): void {
        // TODO проверить на страницах, вынести в createdFilters.submit
        this.filters.forEach((fl) => {
            if (fl.array && fl.supValue && !fl.select?.includes(fl.supValue)) {
                fl.select.push(fl.supValue);
                fl.supValue = '';
            }
        });
        this.$emit('submit');
    }
}
